import { Component, DestroyRef, effect, inject, input, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { merge, Subject, takeUntil, tap } from 'rxjs';
import moment from 'moment';
import { Filter } from '../../filters';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'cbre-range',
  imports: [
    ReactiveFormsModule,
    MatMomentDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule
],
  templateUrl: './range.component.html',
  styleUrl: './range.component.scss',
})
export class RangeFilterComponent {
  readonly form = input<FormGroup>();
  readonly filter = input<Filter>();
  readonly showLabel = input(true);

  /** Backward-compatible alias for templates using filter$() */
  readonly filter$ = this.filter;

  public fromCtrl: FormControl;
  public toCtrl: FormControl;
  public hasFromCtrlValue = false;
  public hasToCtrlValue = false;

  private _form: FormGroup;
  private readonly _reset$ = new Subject();
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const form = this.form();
      const filter = this.filter();
      if (form) this._form = form;
      if (filter != null && this._form != null) {
        this._setUpFilter();
      }
    });
  }

  private _setUpFilter() {
    if (this.filter$() == null || this._form == null) return;

    this.fromCtrl = this._form.get(this.filter$().key).get('from') as FormControl;
    this.toCtrl = this._form.get(this.filter$().key).get('to') as FormControl;

    this._reset$.next(true);
    merge(this._sanitizeCtrlStram(this.fromCtrl), this._sanitizeCtrlStram(this.toCtrl))
      .pipe(takeUntil(this._reset$), takeUntilDestroyed(this.destroyRef))
      .subscribe(this._checkValuePresence);
  }

  private readonly _checkValuePresence = () => {
    this.hasFromCtrlValue = !!this.fromCtrl?.value;
    this.hasToCtrlValue = !!this.toCtrl?.value;
    this.cdr.markForCheck();
  };

  private _sanitizeCtrlStram(ctrl: FormControl) {
    if (this.filter$()?.dataType === 'date') {
      return ctrl.valueChanges.pipe(
        tap(value => ctrl.setValue(this._sanitizeDateValue(value), { emitEvent: false, onlySelf: true }))
      );
    }
    return ctrl.valueChanges;
  }

  private _sanitizeDateValue(value: string) {
    const utcInputFormat = `${moment.HTML5_FMT.DATETIME_LOCAL}Z`; // this is the resulting value => 'YYYY-MM-DDTHH:mmZ'
    const utcOutputFormat = 'YYYY-MM-DDTHH:mm:ss[Z]';
    const dateValue = moment(value, utcInputFormat).utc();
    return dateValue.isValid() ? dateValue.format(utcOutputFormat) : null;
  }

  clearRangeFilter(filterType: 'from' | 'to') {
    if (filterType === 'from') {
      this.fromCtrl.setValue(null);
      return;
    }
    this.toCtrl.setValue(null);
  }

}
